import { useState, useEffect, useCallback } from "react";
import { Button, Input, Switch, TextArea, cn } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { Prompt } from "@webuild-ai/admin/models/components";
import { useUser } from "@webuild-ai/react-auth";

import { ModalOverlay, ModalHeader, ModalBody, ModalFooter } from "@/components/UI/ModalOverlay";
import { availableLanguages } from "@/i18n";
import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { TagSelector } from "@/components/TagSelector/TagSelector";
import { blurActiveElement } from "@/utils/dom";
import { logger } from "@/telemetry";

interface SavePromptModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  promptToSave: string;
  initialData?: {
    title: string;
    description: string;
    tags: string[];
    public: boolean;
  };
  editingPrompt?: Prompt;
  onSuccess?: () => void;
}

export function SavePromptModal({
  isOpen,
  onOpenChange,
  promptToSave,
  initialData,
  editingPrompt,
  onSuccess,
}: SavePromptModalProps) {
  const [tags, setTags] = useState<string[]>([]);
  const [isPublic, setIsPublic] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [content, setContent] = useState(promptToSave);
  const [sourceLanguage, setSourceLanguage] = useState<string>("");
  const [errors, setErrors] = useState<{
    title?: string;
    description?: string;
    content?: string;
    sourceLanguage?: string;
  }>({});
  const { t, i18n } = useTranslation();
  const { adminClient } = useWebuildAPIClient();
  const { userProfile } = useUser();

  // Initialize source language with current app language
  useEffect(() => {
    if (!sourceLanguage) {
      setSourceLanguage(i18n.language);
    }
  }, [i18n.language, sourceLanguage]);

  const resetState = useCallback(() => {
    setTitle("");
    setDescription("");
    setTags([]);
    setIsPublic(false);
    setSourceLanguage(i18n.language);
    setErrors({});
  }, [i18n.language]);

  useEffect(() => {
    setContent(promptToSave);
  }, [promptToSave]);

  useEffect(() => {
    if (editingPrompt) {
      setTitle(editingPrompt.name);
      setDescription(editingPrompt.description);
      setTags(editingPrompt.tags || []);
      setIsPublic(editingPrompt.isPublic || false);
      setSourceLanguage(editingPrompt.sourceLanguage || i18n.language);
    } else if (initialData) {
      setTitle(initialData.title);
      setDescription(initialData.description);
      setTags(initialData.tags);
      setIsPublic(initialData.public);
      setSourceLanguage(i18n.language);
    } else {
      resetState();
    }
  }, [editingPrompt, initialData, i18n.language, resetState]);

  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen, resetState]);

  // Handle modal close with focus cleanup
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      blurActiveElement();
    }
    onOpenChange(open);
  };

  const validateForm = () => {
    const newErrors: typeof errors = {};

    if (!title.trim()) {
      newErrors.title = t("Title is required");
    }

    if (!description.trim()) {
      newErrors.description = t("Description is required");
    }

    if (!content.trim()) {
      newErrors.content = t("Content is required");
    }

    if (!sourceLanguage) {
      newErrors.sourceLanguage = t("Source language is required");
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const clearFieldError = (field: keyof typeof errors) => {
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };

        delete newErrors[field];

        return newErrors;
      });
    }
  };

  const handleSave = async () => {
    if (!adminClient || !userProfile?.tenant) {
      logger.error("Admin client or user profile not found");

      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      if (editingPrompt) {
        await adminClient.prompts.update({
          id: editingPrompt.id,
          prompt: {
            id: editingPrompt.id,
            tenant: userProfile.tenant,
            username: userProfile.id,
            name: title,
            description: description,
            content: content,
            tags: tags,
            isPublic: isPublic,
            sourceLanguage: sourceLanguage,
          },
        });
      } else {
        await adminClient.prompts.createPrompt({
          id: crypto.randomUUID(),
          tenant: userProfile.tenant,
          username: userProfile.id,
          name: title,
          description: description,
          content: content,
          tags: tags,
          isPublic: isPublic,
          sourceLanguage: sourceLanguage,
        });
      }

      handleOpenChange(false);
      onSuccess?.();
    } catch (error) {
      logger.error("Error saving prompt", error);
    }
  };

  if (!isOpen) return null;

  const requiredMark = <span className="text-danger ml-0.5">*</span>;

  return (
    <ModalOverlay isOpen={isOpen} size="3xl" onOpenChange={handleOpenChange}>
      <ModalHeader className="flex flex-col gap-1 font-bold text-brand-primary dark:text-white!">
        {editingPrompt ? t("Edit Prompt") : t("Add New Prompt")}
      </ModalHeader>
      <ModalBody>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium" htmlFor="save-prompt-title">
              {t("Title")}
              {requiredMark}
            </label>
            <Input
              autoFocus
              className={cn(
                "w-full rounded-xl px-3 py-2 bg-default-100 text-sm border focus:outline-none",
                errors.title ? "border-danger" : "border-default-200",
              )}
              data-id="save-prompt-title"
              id="save-prompt-title"
              placeholder={t("Enter a title for your prompt")}
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                clearFieldError("title");
              }}
            />
            {errors.title && <p className="text-danger text-xs mt-1">{errors.title}</p>}
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium" htmlFor="save-prompt-description">
              {t("Description")}
              {requiredMark}
            </label>
            <Input
              className={cn(
                "w-full rounded-xl px-3 py-2 bg-default-100 text-sm border focus:outline-none",
                errors.description ? "border-danger" : "border-default-200",
              )}
              data-id="save-prompt-description"
              id="save-prompt-description"
              placeholder={t("Enter a description")}
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                clearFieldError("description");
              }}
            />
            {errors.description && <p className="text-danger text-xs mt-1">{errors.description}</p>}
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium" htmlFor="save-prompt-language">
              {t("Source Language")}
              {requiredMark}
            </label>
            <select
              required
              className={cn(
                "w-full text-sm border rounded-xl px-3 py-2 bg-default-100 focus:outline-none",
                errors.sourceLanguage ? "border-danger" : "border-default-200",
              )}
              data-id="save-prompt-language"
              id="save-prompt-language"
              value={sourceLanguage}
              onChange={(e) => {
                setSourceLanguage(e.target.value);
                clearFieldError("sourceLanguage");
              }}
            >
              <option value="">{t("Select source language")}</option>
              {availableLanguages.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.nativeName}
                </option>
              ))}
            </select>
            {errors.sourceLanguage && (
              <p className="text-danger text-xs mt-1">{errors.sourceLanguage}</p>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium" htmlFor="save-prompt-content">
              {t("Content")}
              {requiredMark}
            </label>
            <TextArea
              className={cn(
                "w-full rounded-xl px-3 py-2 bg-default-100 text-sm border focus:outline-none resize-y",
                errors.content ? "border-danger" : "border-default-200",
              )}
              data-id="save-prompt-content"
              id="save-prompt-content"
              minRows={5}
              placeholder={t("Prompt content")}
              value={content}
              onChange={(e) => {
                setContent(e.target.value);
                clearFieldError("content");
              }}
            />
            {errors.content && <p className="text-danger text-xs mt-1">{errors.content}</p>}
          </div>

          <div className="space-y-2">
            <TagSelector
              label={t("Tags")}
              selectedTags={tags}
              showAddNewTag={true}
              onTagsChange={setTags}
            />
            <div className="flex items-center gap-2 pt-1">
              <Switch data-id="save-prompt-public" isSelected={isPublic} onChange={setIsPublic}>
                <Switch.Content>
                  <Switch.Control>
                    <Switch.Thumb />
                  </Switch.Control>
                </Switch.Content>
              </Switch>
              <span className="text-xs text-gray-500">
                {isPublic ? t("Shared with company") : t("Only you can access")}
              </span>
            </div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          className="btn-brand-primary text-sm"
          data-id="save-prompt-cancel"
          onPress={() => handleOpenChange(false)}
        >
          {t("Cancel")}
        </Button>
        <Button
          className="btn-brand-primary text-sm"
          data-id="save-prompt-save-button"
          onPress={handleSave}
        >
          {t("Save")}
        </Button>
      </ModalFooter>
    </ModalOverlay>
  );
}
